/**
 * Elementi condivisi della vista "Batch Edits": badge di stato, barra di progresso, etichette,
 * formattazione e aggregazioni sulle statistiche dei record.
 */
import type {
  BatchHold,
  BatchStatus,
  BatchSummary,
  RecordKind,
  RecordStats,
  RecordStatus,
} from './types.js';
import styles from './BatchEdits.module.css';

export const BATCH_STATUS_LABEL: Record<BatchStatus, string> = {
  created: 'Creato',
  uploading: 'Caricamento',
  validated: 'Validato',
  'on-hold': 'In attesa di conferma',
  invalid: 'Non valido',
  queued: 'In coda',
  running: 'In esecuzione',
  completed: 'Completato',
  'completed-with-errors': 'Completato con errori',
  failed: 'Fallito',
};

const BATCH_STATUS_CLASS: Record<BatchStatus, string | undefined> = {
  created: styles.bIdle,
  uploading: styles.bIdle,
  validated: styles.bWarn,
  'on-hold': styles.bHold,
  invalid: styles.bErr,
  queued: styles.bQueue,
  running: styles.bRun,
  completed: styles.bOk,
  'completed-with-errors': styles.bWarn,
  failed: styles.bErr,
};

export const KIND_LABEL: Record<RecordKind, string> = {
  new: 'Nuovo',
  update: 'Aggiornamento',
  delete: 'Cancellazione',
  unchanged: 'Invariato',
};

export const RECORD_STATUS_LABEL: Record<RecordStatus, string> = {
  pending: 'In attesa',
  done: 'Eseguito',
  failed: 'Fallito',
  'pending-dt': 'Analisi in sospeso',
  skipped: 'Nessuna modifica',
  'not-executed': 'Non eseguito',
};

const RECORD_STATUS_CLASS: Record<RecordStatus, string | undefined> = {
  pending: styles.bIdle,
  done: styles.bOk,
  failed: styles.bErr,
  'pending-dt': styles.bWarn,
  skipped: styles.bIdle,
  'not-executed': styles.bErr,
};

export const ENTITY_LABEL: Record<string, string> = {
  product: 'Prodotto',
  release: 'Release',
  component: 'Componente',
  mapping: 'Mapping',
  customer: 'Cliente',
  site: 'Sede',
  contact: 'Contatto',
  deployment: 'Fornitura',
};

export function BatchStatusBadge({ status }: { status: BatchStatus }) {
  return (
    <span
      className={`${styles.badge} ${BATCH_STATUS_CLASS[status] ?? ''}`}
      data-testid={`batch-status-${status}`}
    >
      {BATCH_STATUS_LABEL[status] ?? status}
    </span>
  );
}

/** Motivo dell'hold in una riga: «12 modifiche ≥ soglia 10». */
export function holdShort(hold: BatchHold): string {
  return `${fmtNum(hold.changes)} modifiche ≥ soglia ${fmtNum(hold.threshold)}`;
}

/**
 * Motivo dell'hold per esteso, con la regola di confronto esplicita (si esegue solo sotto soglia).
 */
export function holdLong(hold: BatchHold): string {
  const b = hold.breakdown;
  return (
    `${fmtNum(hold.changes)} modifiche (${fmtNum(b.create + b.reactivate)} nuovi, ` +
    `${fmtNum(b.update)} aggiornamenti, ${fmtNum(b.delete)} cancellazioni) non sono sotto la ` +
    `soglia di ${fmtNum(hold.threshold)}: un batch parte automaticamente soltanto con meno di ` +
    `${fmtNum(hold.threshold)} modifiche (gli invariati non contano).`
  );
}

export function RecordStatusBadge({ status }: { status: RecordStatus }) {
  return (
    <span className={`${styles.badge} ${RECORD_STATUS_CLASS[status] ?? ''}`}>
      {RECORD_STATUS_LABEL[status] ?? status}
    </span>
  );
}

export function ProgressBar({ batch }: { batch: Pick<BatchSummary, 'status' | 'progress'> }) {
  const pct = batch.progress.percent;
  const fill =
    batch.status === 'failed'
      ? styles.progressFillErr
      : batch.status === 'completed' || batch.status === 'completed-with-errors'
        ? styles.progressFillDone
        : '';
  return (
    <div
      className={styles.progress}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      aria-label="Avanzamento esecuzione"
    >
      <div className={styles.progressTrack}>
        <div className={`${styles.progressFill} ${fill}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={styles.progressLabel}>{pct}%</span>
    </div>
  );
}

export function fmtDateTime(iso?: string): string {
  if (!iso) return '–';
  return new Date(iso).toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('it-IT', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function fmtDuration(ms?: number): string {
  if (ms === undefined || ms < 0) return '–';
  if (ms < 1000) return `${ms} ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return m < 60 ? `${m}m ${s % 60}s` : `${Math.floor(m / 60)}h ${m % 60}m`;
}

export const fmtNum = (n: number): string => n.toLocaleString('it-IT');

/** Numero di record per tipo (dalle statistiche; prima della validazione: dal piano, se c'è). */
export function kindTotals(
  batch: Pick<BatchSummary, 'stats' | 'planSummary'>,
): Record<RecordKind, number> {
  if (batch.stats) return kindTotalsFromStats(batch.stats);
  const p = batch.planSummary;
  return {
    new: (p?.create ?? 0) + (p?.reactivate ?? 0),
    update: p?.update ?? 0,
    delete: p?.delete ?? 0,
    unchanged: p?.unchanged ?? 0,
  };
}

export function kindTotalsFromStats(stats: RecordStats): Record<RecordKind, number> {
  const sum = (k: RecordKind) => Object.values(stats.byKind[k]).reduce((a, b) => a + b, 0);
  return {
    new: sum('new'),
    update: sum('update'),
    delete: sum('delete'),
    unchanged: sum('unchanged'),
  };
}

/** Terna nuovi / aggiornati / cancellati (colorata). */
export function KindTriplet({ totals }: { totals: Record<RecordKind, number> }) {
  return (
    <span className={styles.countTriplet}>
      <span className={totals.new ? styles.cNew : styles.cDim} title="Nuovi record">
        +{fmtNum(totals.new)}
      </span>
      <span className={totals.update ? styles.cUpd : styles.cDim} title="Aggiornamenti">
        ~{fmtNum(totals.update)}
      </span>
      <span className={totals.delete ? styles.cDel : styles.cDim} title="Cancellazioni">
        −{fmtNum(totals.delete)}
      </span>
    </span>
  );
}
